import type { Canal, IdentificadorLoja } from '../api';

/**
 * Mapeia o tipo do identificador para o canal do catálogo (filtro de
 * visibilidade do GrupoProduto/Produto).
 */
export function canalDoIdentificador(identificador: IdentificadorLoja): Canal {
  switch (identificador.tipo) {
    case 'MESA':
    case 'COMANDA':
    case 'TABLET':
      // tablet fixo na mesa usa o mesmo catálogo/filtros de mesa/comanda
      return 'CARDAPIO_COMANDA_MESA';
    case 'DELIVERY':
      return 'CARDAPIO_DELIVERY';
    case 'VITRINE':
      return 'CARDAPIO_VITRINE';
    case 'TOTEM':
      return 'CARDAPIO_TOTEM';
    case 'TOTEM_CHECKOUT':
      return 'CARDAPIO_TOTEM_CHECKOUT';
  }
}

/** Rótulo do modo/tipo de pedido atual, para exibir ao cliente. */
export function rotuloTipoPedido(tipo: IdentificadorLoja['tipo']): string {
  switch (tipo) {
    case 'MESA':
      return 'Pedido de mesa';
    case 'COMANDA':
      return 'Pedido de comanda';
    case 'TOTEM':
      return 'Pedido de totem';
    case 'TABLET':
      return 'Pedido de mesa';
    case 'TOTEM_CHECKOUT':
      return 'Totem de checkout';
    case 'DELIVERY':
      return 'Pedido de delivery';
    case 'VITRINE':
      return 'Modo vitrine';
    default:
      return '';
  }
}

export function ehVitrine(identificador: IdentificadorLoja): boolean {
  return identificador.tipo === 'VITRINE';
}

/** Modo kiosk: dispositivo da loja, autoatendimento. */
export function ehTotem(identificador: IdentificadorLoja): boolean {
  return identificador.tipo === 'TOTEM';
}

/** Totem de checkout: kiosk de autoatendimento para fechamento/pagamento de comanda. */
export function ehTotemCheckout(identificador: IdentificadorLoja): boolean {
  return identificador.tipo === 'TOTEM_CHECKOUT';
}

/** Tablet fixo na mesa: dispositivo da loja, com conta e chamar garçom. */
export function ehTablet(identificador: IdentificadorLoja): boolean {
  return identificador.tipo === 'TABLET';
}

/** Aparelho da loja que digita pela faixa do `TecladoTela`; nele o teclado do sistema fica calado. */
export function usaTecladoDeTela(identificador: IdentificadorLoja): boolean {
  return ehTotem(identificador) || ehTotemCheckout(identificador) || ehTablet(identificador);
}

/** Cardápio na mesa via QR (mesa ou comanda) — o cliente usa o próprio celular. */
export function ehMesaComanda(identificador: IdentificadorLoja): boolean {
  return identificador.tipo === 'MESA' || identificador.tipo === 'COMANDA';
}

/**
 * Atendimento no salão (tablet fixo OU mesa/comanda por QR): tem os atalhos de
 * "chamar garçom" e "ver total consumido", e o pedido vai pra comanda/mesa.
 */
export function ehAtendimentoSalao(identificador: IdentificadorLoja): boolean {
  return ehTablet(identificador) || ehMesaComanda(identificador);
}

/**
 * Identificação da mesa/comanda para exibir e para os atalhos de salão.
 *  - MESA/COMANDA (QR): vem do próprio identificador (tipo + número).
 *  - TABLET: a mesa é configurada no aparelho (número informado à parte).
 */
export function identificacaoSalao(
  identificador: IdentificadorLoja,
  mesaTablet?: string,
): { tipo: 'MESA' | 'COMANDA'; numero: string; rotulo: string } {
  if (ehTablet(identificador)) {
    const numero = (mesaTablet ?? '').trim();
    return { tipo: 'MESA', numero, rotulo: `Mesa ${numero || '—'}` };
  }
  const tipo = identificador.tipo === 'COMANDA' ? 'COMANDA' : 'MESA';
  const numero = identificador.numero ?? '';
  return { tipo, numero, rotulo: `${tipo === 'COMANDA' ? 'Comanda' : 'Mesa'} ${numero || '—'}` };
}

/** Dispositivo fixo da loja (kiosk): totem, totem de checkout ou tablet — fixa o tenant no aparelho. */
export function ehDispositivoFixo(identificador: IdentificadorLoja): boolean {
  return ehTotem(identificador) || ehTotemCheckout(identificador) || ehTablet(identificador);
}
